/**
 * ----------------------------------------------------------------------------
 * COUNTDOWN TIMER
 * ----------------------------------------------------------------------------
 * Custom addition to the Impact theme. Not part of Maestrooo's base code, so it
 * lives in its own asset and is loaded only by the section that needs it.
 *
 * ********************************************
 * The server already rendered a usable banner
 * ********************************************
 *
 * The section prints the target date in plain French before this script runs,
 * and the numbers below start at their server-computed values. This file only
 * keeps them ticking. If it fails to load, or the shopper blocks scripts, the
 * banner still says when the drop lands instead of showing an empty frame or a
 * row of zeros.
 *
 * ********************************************
 * Timezone
 * ********************************************
 *
 * The target is emitted as a full ISO instant by Liquid, resolved in the shop's
 * timezone. Parsing it here in the visitor's browser is therefore correct for a
 * visitor anywhere: everyone counts down to the same moment, and a shopper in
 * Réunion does not see the drop unlock four hours early.
 */

const SECOND = 1000;
const MINUTE = SECOND * 60;
const HOUR = MINUTE * 60;
const DAY = HOUR * 24;

/**
 * Countdown to a fixed instant, ticking once per second.
 *
 * Attributes:
 *   target  - the deadline as an ISO 8601 string
 *   expired - reflected on the element once the deadline has passed, so CSS can react
 */
class CountdownTimer extends HTMLElement {
  connectedCallback() {
    const target = Date.parse(this.getAttribute('target'));

    /* An unparseable date would render NaN in every slot. Leave the server output alone instead. */
    if (Number.isNaN(target)) {
      return;
    }

    this.target = target;
    this.slots = {
      days: this.querySelector('[data-countdown-days]'),
      hours: this.querySelector('[data-countdown-hours]'),
      minutes: this.querySelector('[data-countdown-minutes]'),
      seconds: this.querySelector('[data-countdown-seconds]'),
    };

    this.tick();
    this.interval = setInterval(() => this.tick(), SECOND);
  }

  disconnectedCallback() {
    clearInterval(this.interval);
  }

  /**
   * Refresh the four slots from the remaining time, and stop once the deadline passes.
   *
   * @returns {void}
   */
  tick() {
    const remaining = this.target - Date.now();

    if (remaining <= 0) {
      clearInterval(this.interval);
      this.setAttribute('expired', '');
      Object.values(this.slots).forEach((slot) => slot && (slot.textContent = '00'));
      this.dispatchEvent(new CustomEvent('countdown:expired', { bubbles: true }));
      return;
    }

    this.write(this.slots.days, Math.floor(remaining / DAY));
    this.write(this.slots.hours, Math.floor((remaining % DAY) / HOUR));
    this.write(this.slots.minutes, Math.floor((remaining % HOUR) / MINUTE));
    this.write(this.slots.seconds, Math.floor((remaining % MINUTE) / SECOND));
  }

  /**
   * Write a zero-padded value into a slot, skipping the DOM write when unchanged.
   *
   * @param {HTMLElement|null} slot - The element holding the number
   * @param {number} value - The value to display
   * @returns {void}
   */
  write(slot, value) {
    if (!slot) {
      return;
    }

    const text = String(value).padStart(2, '0');

    if (slot.textContent !== text) {
      slot.textContent = text;
    }
  }
}

if (!customElements.get('countdown-timer')) {
  customElements.define('countdown-timer', CountdownTimer);
}
